import { Icon, IconProps } from 'almanac-ui/icons/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Blog as BlogComponent } from 'almanac-ui/icons/blaze/24/Blog'
import {
  AbandonedCart,
  AboutUs,
  BattleCard,
  BillingAndPayments,
  Briefs,
  CallScript,
  CandidateNurture,
  Care,
  CaseStudies,
  Cold,
  DidntSignUp,
  Faq,
  HelpDocs,
  Impact,
  JobDescription,
  LandingPage,
  LostCustomer,
  MailNurture,
  MailPromo,
  Mission,
  OnePager,
  Outline,
  PaymentConfirmation,
  PreCallChecklist,
  PressRelease,
  Pricing,
  ReplyTemplates,
  Research,
  ReturnPolicy,
  ShippingPolicy,
  Sms,
  Testimonial,
  Warm,
  WelcomeSeries,
  ProductDemo,
} from 'almanac-ui/icons/blaze/32'
import Amazon from 'almanac-ui/icons/blaze/18/Amazon'
import Bing from 'almanac-ui/icons/blaze/18/Bing'
import { Facebook, Google, Instagram, LinkedIn, TikTok, Twitter, Youtube } from 'almanac-ui/icons'
import Pinterest from 'almanac-ui/icons/blaze/18/Pinterest'
import SquareSpace from 'almanac-ui/icons/blaze/18/SquareSpace'
import Shopify from 'almanac-ui/icons/blaze/28/Shopify'
import Etsy from 'almanac-ui/icons/blaze/28/Etsy'
import cx from 'classnames'
import Styles from './ChannelIcon.module.scss'
import { DesignChannel } from 'components/NewFileModal/Types'
import { absurd } from 'types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Mercari, WhatsApp, Ebay, Poshmark, Depop, Shopee, Lazada, Vinted, Design, Mercadolibre } from 'blaze-ui/icons'
import { ContentPlan } from 'blaze-ui/icons/36'
import { Brainstorm } from 'blaze-ui/icons/24/Brainstorm'
import { LinkedInBrand } from 'blaze-ui/icons/35/LinkedInBrand'
import { InstagramBrand } from 'blaze-ui/icons/24/InstagramBrand'
import { FacebookBrand } from 'blaze-ui/icons/35/FacebookBrand'
import { MetaBrand } from 'blaze-ui/icons/20/MetaBrand'
import { TwitterBrand } from 'blaze-ui/icons/35/TwitterBrand'
import { TiktokBrand } from 'blaze-ui/icons/35/TiktokBrand'
import { DynamicHighDetailedMail } from 'blaze-ui/icons/40'

interface Props extends IconProps {
  channel: ChannelType | DesignChannel | GenerationType | undefined
  defaultIcon?: Icon
  isWhite?: boolean
  isTransparentBackground?: boolean
  blogPlatform?: ExternalIntegrationEnum | null
  // Legacy props - kept for backward compatibility, will be deprecated
  // @deprecated - use blogPlatform instead
  useGoHighLevelBranding?: boolean
  // @deprecated - use blogPlatform instead
  useWordPressBranding?: boolean
}

export const ChannelIcon = ({
  channel,
  size = 16,
  defaultIcon: DefaultIcon = BlogComponent,
  isWhite = false,
  isTransparentBackground = true,
  blogPlatform,
  useGoHighLevelBranding = false,
  useWordPressBranding = false,
  ...rest
}: Props) => {
  const iconProps = { size, ...rest }
  const resolvedUseGoHighLevelBranding = blogPlatform
    ? blogPlatform === ExternalIntegrationEnum.GoHighLevel
    : useGoHighLevelBranding

  const resolvedUseWordPressBranding = blogPlatform
    ? blogPlatform === ExternalIntegrationEnum.WordPress
    : useWordPressBranding
  switch (channel) {
    case ChannelType.PromotionalEmailSequence:
      return <MailPromo {...iconProps} />
    case ChannelType.SMSMessages:
      return <Sms {...iconProps} />
    case ChannelType.Newsletter:
      return <DynamicHighDetailedMail {...iconProps} />
    case ChannelType.AmazonAds:
    case ChannelType.ProductLaunchAmazon:
    case DesignChannel.Amazon:
      return (
        <div className={cx(Styles.iconContainer, Styles.amazon, { [Styles.isWhite]: isWhite })}>
          <Amazon {...iconProps} />
        </div>
      )
    case ChannelType.BingAds:
      return <Bing {...iconProps} />
    case ChannelType.GoogleAds:
    case ChannelType.ProductLaunchGoogle:
    case DesignChannel.GoogleMyBusinessPost:
      return (
        <div className={cx(Styles.iconContainer, Styles.google, { [Styles.isWhite]: isWhite })}>
          <Google {...iconProps} />
        </div>
      )
    case DesignChannel.MetaAds:
      return (
        <div className={Styles.iconContainer} style={{ height: size, width: size }}>
          <MetaBrand size={size} />
        </div>
      )
    case ChannelType.FacebookAds:
    case ChannelType.FacebookPost:
    case DesignChannel.FacebookCover:
    case DesignChannel.FacebookProfilePicture:
    case DesignChannel.FacebookPost:
    case DesignChannel.FacebookStory:
    case DesignChannel.FacebookMarketplace:
    case GenerationType.FacebookPlanning:
      if (!isTransparentBackground) {
        return (
          <div className={Styles.iconContainer} style={{ height: size, width: size }}>
            <FacebookBrand />
          </div>
        )
      }

      return (
        <div className={cx(Styles.iconContainer, Styles.facebook, { [Styles.isWhite]: isWhite })}>
          <Facebook {...iconProps} />
        </div>
      )
    case ChannelType.PinterestAds:
    case DesignChannel.Pinterest:
      if (!isTransparentBackground) {
        return (
          <div className={cx(Styles.iconContainer, Styles.pinterest)} style={{ height: size, width: size }}>
            <Pinterest {...iconProps} />
          </div>
        )
      }

      return <Pinterest {...iconProps} />
    case ChannelType.TikTokAds:
    case ChannelType.TikTokScript:
    case DesignChannel.TikTokPost:
    case DesignChannel.TikTokThumbnail:
      if (!isTransparentBackground) {
        return (
          <div className={Styles.iconContainer} style={{ height: size, width: size }}>
            <TiktokBrand />
          </div>
        )
      }

      return (
        <div className={cx(Styles.iconContainer, Styles.tikTok, { [Styles.isWhite]: isWhite })}>
          <TikTok {...iconProps} />
        </div>
      )
    case DesignChannel.InstagramPost:
    case ChannelType.InstagramPosts:
    case ChannelType.InstagramCaptions:
    case ChannelType.InstagramReel:
    case DesignChannel.InstagramReel:
    case DesignChannel.InstagramStory:
    case GenerationType.InstagramPlanning:
      if (!isTransparentBackground) {
        return (
          <div className={Styles.iconContainer} style={{ height: size, width: size }}>
            <InstagramBrand />
          </div>
        )
      }

      return (
        <div className={cx(Styles.iconContainer, Styles.instagram, { [Styles.isWhite]: isWhite })}>
          <Instagram {...iconProps} />
        </div>
      )
    case DesignChannel.LinkedInPost:
    case ChannelType.LinkedInAds:
    case ChannelType.LinkedInPosts:
    case DesignChannel.LinkedInBanner:
    case DesignChannel.LinkedInProfilePicture:
    case GenerationType.LinkedInPlanning:
      if (!isTransparentBackground) {
        return (
          <div className={Styles.iconContainer} style={{ height: size, width: size }}>
            <LinkedInBrand />
          </div>
        )
      }

      return (
        <div className={cx(Styles.iconContainer, Styles.linkedIn, { [Styles.isWhite]: isWhite })}>
          <LinkedIn {...iconProps} />
        </div>
      )
    case ChannelType.Tweets:
    case ChannelType.TweetThread:
    case DesignChannel.TwitterPost:
    case DesignChannel.TwitterCoverImage:
    case DesignChannel.TwitterProfilePicture:
    case GenerationType.TwitterPlanning:
      if (!isTransparentBackground) {
        return (
          <div className={Styles.iconContainer} style={{ height: size, width: size }}>
            <TwitterBrand />
          </div>
        )
      }

      return (
        <div className={cx(Styles.iconContainer, Styles.x, { [Styles.isWhite]: isWhite })}>
          <Twitter {...iconProps} />
        </div>
      )
    case ChannelType.LandingPage:
      return <LandingPage {...iconProps} />
    case ChannelType.ProductLaunchWebsite:
      return (
        <div className={cx(Styles.iconContainer, Styles.squareSpace, { [Styles.isWhite]: isWhite })}>
          <SquareSpace {...iconProps} />
        </div>
      )
    case ChannelType.ProductLaunchShopify:
    case DesignChannel.Shopify:
      return (
        <div className={cx(Styles.iconContainer, Styles.shopify, { [Styles.isWhite]: isWhite })}>
          <Shopify {...iconProps} />
        </div>
      )
    case ChannelType.ProductLaunchEtsy:
    case DesignChannel.Etsy:
      return (
        <div className={cx(Styles.iconContainer, Styles.etsy, { [Styles.isWhite]: isWhite })}>
          <Etsy {...iconProps} />
        </div>
      )
    case ChannelType.AboutUs:
      return <AboutUs {...iconProps} />
    case ChannelType.Pricing:
      return <Pricing {...iconProps} />
    case ChannelType.FAQs:
      return <Faq {...iconProps} />
    case ChannelType.Mission:
      return <Mission {...iconProps} />
    case ChannelType.Impact:
      return <Impact {...iconProps} />
    case ChannelType.JobDescription:
      return <JobDescription {...iconProps} />
    case ChannelType.CandidateNurtureSequence:
      return <CandidateNurture {...iconProps} />
    case ChannelType.BlogIdeas:
    case GenerationType.Brainstorm:
      return <Brainstorm {...iconProps} />
    case GenerationType.ContentBrief:
      return <Briefs {...iconProps} />
    case GenerationType.Outline:
      return <Outline {...iconProps} />
    case GenerationType.ContentPlan:
      return <ContentPlan {...iconProps} />
    case ChannelType.CaseStudy:
      return <CaseStudies {...iconProps} />
    case ChannelType.PressRelease:
      return <PressRelease {...iconProps} />
    case ChannelType.ColdOutboundEmailSequence:
      return <Cold {...iconProps} />
    case ChannelType.WarmOutboundEmailSequence:
      return <Warm {...iconProps} />
    case ChannelType.YoutubeScript:
    case ChannelType.YoutubeAds:
    case DesignChannel.YoutubeProfilePicture:
    case DesignChannel.YoutubeChannelArt:
    case DesignChannel.YoutubeShort:
      return (
        <div className={cx(Styles.iconContainer, Styles.youtube, { [Styles.isWhite]: isWhite })}>
          <Youtube {...iconProps} />
        </div>
      )
    case ChannelType.NewsletterIdeas:
      return <Brainstorm {...iconProps} />
    case ChannelType.NurtureEmailSequence:
      return <MailNurture {...iconProps} />
    case ChannelType.WelcomeSeries:
      return <WelcomeSeries {...iconProps} />
    case ChannelType.DidntSignUp:
      return <DidntSignUp {...iconProps} />
    case ChannelType.AbandonedCart:
      return <AbandonedCart {...iconProps} />
    case ChannelType.LostCustomer:
      return <LostCustomer {...iconProps} />
    case ChannelType.OnePager:
      return <OnePager {...iconProps} />
    case ChannelType.BattleCard:
      return <BattleCard {...iconProps} />
    case ChannelType.Testimonial:
      return <Testimonial {...iconProps} />
    case ChannelType.ProductDemo:
      return <ProductDemo {...iconProps} />
    case ChannelType.PreCallChecklist:
      return <PreCallChecklist {...iconProps} />
    case ChannelType.CallScript:
      return <CallScript {...iconProps} />
    case ChannelType.Research:
      return <Research {...iconProps} />
    case ChannelType.ReplyTemplates:
      return <ReplyTemplates {...iconProps} />
    case ChannelType.HelpDocs:
      return <HelpDocs {...iconProps} />
    case ChannelType.ReturnPolicy:
      return <ReturnPolicy {...iconProps} />
    case ChannelType.ShippingPolicy:
      return <ShippingPolicy {...iconProps} />
    case ChannelType.CareInstructions:
      return <Care {...iconProps} />
    case ChannelType.BillingAndPayments:
      return <BillingAndPayments {...iconProps} />
    case ChannelType.PaymentConfirmation:
      return <PaymentConfirmation {...iconProps} />
    case ChannelType.BlogPost:
      return (
        <BlogComponent
          {...iconProps}
          blogPlatform={blogPlatform}
          useGoHighLevelBranding={resolvedUseGoHighLevelBranding}
          useWordPressBranding={resolvedUseWordPressBranding}
        />
      )
    case DesignChannel.WhatsAppSticker:
      return <WhatsApp {...iconProps} />
    case DesignChannel.Ebay:
      if (!isTransparentBackground) {
        return (
          <div className={cx(Styles.iconContainer, Styles.ebay)} style={{ height: size, width: size }}>
            <Ebay {...iconProps} />
          </div>
        )
      }

      return <Ebay {...iconProps} />
    case DesignChannel.Poshmark:
      return <Poshmark {...iconProps} />
    case DesignChannel.Depop:
      return <Depop {...iconProps} />
    case DesignChannel.Mercari:
      return <Mercari {...iconProps} />
    case DesignChannel.MercadoLibre:
      return <Mercadolibre {...iconProps} />
    case DesignChannel.Shopee:
      return <Shopee {...iconProps} />
    case DesignChannel.Lazada:
      return <Lazada {...iconProps} />
    case DesignChannel.Vinted:
      return <Vinted {...iconProps} />
    case DesignChannel.Post:
      if (!isTransparentBackground) {
        return (
          <div className={cx(Styles.iconContainer, Styles.designDefault)} style={{ height: size, width: size }}>
            <Design {...iconProps} />
          </div>
        )
      }

      return <Design {...iconProps} />
    case GenerationType.Article:
    case GenerationType.Generic:
    case GenerationType.InlineGeneric:
    case GenerationType.Campaign:
    case GenerationType.SEOPlanning:
    case GenerationType.Image:
    case undefined:
      return <DefaultIcon {...iconProps} />
    default:
      absurd(channel)
      return <DefaultIcon {...iconProps} />
  }
}
